import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { isEqual } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { filter, pairwise, startWith } from 'rxjs/operators';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { DocumentType, EntityType, PurchaseOrderStatus } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { PurchaseOrdersService } from '@shared/store/purchase-orders/purchase-orders.service';
import { PurchaseOrdersState } from '@shared/store/purchase-orders/purchase-orders.store';
import { Option } from '@shared/types/components.type';

export interface NewPoDialogData {
  isPoEditableByPivot?: boolean;
  mode: 'add' | 'update';
  po_id?: string;
  vendorId?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FileManagerComponent,
    InputComponent,
    ReactiveFormsModule,
    NgSelectModule,
    FileViewerComponent,
    AsyncPipe,
    FormErrorDirective,
    TooltipDirective,
  ],
  selector: 'aux-new-po-dialog',
  templateUrl: './new-po-dialog.component.html',
})
export class NewPoDialogComponent implements OnInit {
  ref = inject(
    CustomOverlayRef<
      unknown,
      { isPoEditableByPivot?: boolean; mode: 'add' | 'update'; po_id?: string; vendorId?: string }
    >
  );
  vendorQuery = inject(OrganizationQuery);

  private destroyRef = inject(DestroyRef);
  private fb = inject(UntypedFormBuilder);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private purchaseOrdersQuery = inject(PurchaseOrdersQuery);
  private purchaseOrdersService = inject(PurchaseOrdersService);

  defaultFgValue: {
    description: null | string | undefined;
    po_number: string | undefined;
    po_status: null | PurchaseOrderStatus | undefined;
  } = {
    description: '',
    po_number: '',
    po_status: null,
  };
  fg = this.fb.group({
    description: '',
    po_number: ['', [Validators.required]],
    po_status: [null, [Validators.required]],
  });
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  hasChanges = false;
  isPoEditableByPivot = true;
  loading$ = new BehaviorSubject(false);
  mode: 'add' | 'update' = 'add';
  poId = '';
  poInitialNumber = '';
  poNumbers: PurchaseOrdersState[] = [];
  poNumberValidators: ValidatorFn[] = [
    (control) => {
      const value: string = control.value;

      if (!value) {
        return { required: true };
      }

      const poNumber = this.poNumbers.find(
        (po) => po.po_number.toLowerCase() === value.toLowerCase()
      );

      return poNumber && poNumber.po_number !== this.poInitialNumber
        ? { duplicatedPO: true }
        : null;
    },
  ];
  readonly poStatusOptions: Option[] = [
    { label: 'Open', value: PurchaseOrderStatus.PO_STATUS_OPEN },
    { label: 'Closed', value: PurchaseOrderStatus.PO_STATUS_CLOSED },
  ];
  selectedVendor = new UntypedFormControl(null, [Validators.required]);

  ngOnInit() {
    this.selectedVendor.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.hasChanges = true));

    this.fg.valueChanges
      .pipe(
        startWith(this.defaultFgValue),
        pairwise(),
        filter(([prev, next]) => !isEqual(prev, next)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.hasChanges = true;
      });
  }

  constructor() {
    this.purchaseOrdersQuery
      .selectAll()
      .pipe(takeUntilDestroyed())
      .subscribe((allPO) => {
        this.poNumbers = allPO || [];
      });

    if (this.ref.data?.mode) {
      this.mode = this.ref.data?.mode;
    }

    if (this.ref.data?.po_id) {
      this.poId = this.ref.data.po_id;
      this.isPoEditableByPivot = !!this.ref.data.isPoEditableByPivot;
      const po = this.purchaseOrdersQuery.getEntity(this.ref.data.po_id);

      if (po) {
        this.fg.patchValue({
          description: po.description,
          po_number: po.po_number,
          po_status: po.po_status,
        });
        this.defaultFgValue = {
          description: po.description,
          po_number: po.po_number,
          po_status: po.po_status,
        };
        this.selectedVendor.setValue(po.organization_id);
        this.poInitialNumber = po.po_number || '';

        if (!this.ref.data.isPoEditableByPivot) {
          this.fg.controls['po_number'].disable();
          this.selectedVendor.disable();
        }
      }
    }

    if (this.ref.data?.vendorId) {
      this.selectedVendor.setValue(this.ref.data?.vendorId);
    }
  }

  disabledTooltip(): string {
    return this.mode === 'update' && !this.isPoEditableByPivot
      ? MessagesConstants.PO.PO_USED_FOR_CLOSED_CALCULATION
      : '';
  }

  getFilePath(poID: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/purchaseorders/${poID}/`;
  }

  isSaveButtonDisabled(): boolean {
    return !this.hasChanges || !this.fg.valid || !this.selectedVendor.value;
  }

  // eslint-disable-next-line max-lines-per-function
  async onUpload() {
    if (
      this.fg.valid &&
      !this.loading$.getValue() &&
      this.fileManager &&
      this.selectedVendor.value
    ) {
      this.loading$.next(true);
      const { description, po_number, po_status } = this.fg.value;
      const organization_id = this.selectedVendor.value;

      if (this.mode === 'add') {
        const { data, success } = await this.purchaseOrdersService.add({
          description,
          organization_id,
          po_number,
          po_status,
        });

        if (success && data) {
          const files = this.fileManager.fileQuery.getAll();

          files.forEach((file) => {
            const bucket_key = `${this.getFilePath(data.id)}${file.bucket_key}`;

            this.fileManager?.fileStore?.update(file.id, {
              ...file,
              bucket_key,
            });
          });

          const filesSuccess = await this.fileManager.fileService.uploadFiles({
            insertDocument: true,
            metadata: {
              documentType: DocumentType.DOCUMENT_PURCHASE_ORDER,
              entity_id: data.id,
              entity_type_id: EntityType.PURCHASE_ORDER,
              vendor: organization_id,
            },
            showSuccess: false,
          });

          // eslint-disable-next-line max-depth
          if (filesSuccess) {
            this.overlayService.success(`Purchase order successfully added!`);
            this.purchaseOrdersService.PONumbers$.next([
              ...this.purchaseOrdersService.PONumbers$.value,
              { id: data.id, po_number },
            ]);
            this.ref.close(true);
          }
        }
      } else if (this.ref.data?.po_id) {
        const files = this.fileManager.fileQuery.getAll();

        files.forEach((file) => {
          const bucket_key = `${this.getFilePath(this.ref.data?.po_id || '')}${file.bucket_key}`;

          this.fileManager?.fileStore?.update(file.id, {
            ...file,
            bucket_key,
          });
        });
        const { success } = await this.purchaseOrdersService.update({
          description,
          id: this.ref.data.po_id,
          organization_id: this.selectedVendor.value,
          po_number: this.fg.controls['po_number'].value,
          po_status,
        });

        if (success) {
          const filesSuccess = await this.fileManager.fileService.uploadFiles({
            insertDocument: true,
            metadata: {
              documentType: DocumentType.DOCUMENT_PURCHASE_ORDER,
              entity_id: this.ref.data.po_id,
              entity_type_id: EntityType.PURCHASE_ORDER,
              vendor: organization_id,
            },
            showSuccess: false,
          });

          // eslint-disable-next-line max-depth
          if (filesSuccess) {
            this.overlayService.success(`Purchase order successfully updated!`);
            this.ref.close(true);
          }
        }
      }

      this.loading$.next(false);
    }
  }

  pathFn = () => '';

  removeFile(file: File): void {
    if (file.uploaded) {
      void this.fileManager?.removeFile(file);
    }

    this.setChanges();
  }

  saveButtonTooltip(): string {
    if (!this.hasChanges) {
      return MessagesConstants.NO_CHANGES_TO_SAVE;
    }

    return !this.fg.valid || !this.selectedVendor.value
      ? MessagesConstants.RESOLVE_ERRORS_TO_CONTINUE
      : '';
  }

  setChanges(): void {
    this.hasChanges = true;
  }
}
